import { Component, OnInit, ElementRef, OnDestroy } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import reduce from 'lodash-es/reduce';
import get from 'lodash-es/get';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { debounceTime, takeUntil } from 'rxjs/operators';
import '@wdpr/single-select';

import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { AvatarCheckboxEvent } from '@finder/shared/components/avatar-checkbox/avatar-checkbox.interface';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { KeyringService } from '@finder/shared/services/keyring/keyring.service';
import { ModalChildComponent } from '@finder/shared/components/modal/modal-child-component.interface';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { MY_PLANS_MODAL_ANIMATIONS } from '@finder/shared/components/my-plans-modal/my-plans-modal.animations';
import { MyPlan } from '@finder/shared/components/my-plans-modal/my-plan.class';
import { MyPlansService } from '@finder/shared/services/my-plans-service/my-plans.service';
import { SCHEDULE_CONSTANTS, SCHEDULE_TYPES } from '@finder/shared/constants/app.constants';
import { DATE_FORMAT_CONSTANTS } from '@finder/shared/constants/date.constants';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';

@Component({
    selector: 'finder-my-plans-modal',
    templateUrl: './my-plans-modal.component.html',
    styleUrls: ['./my-plans-modal.component.scss'],
    animations: [...MY_PLANS_MODAL_ANIMATIONS]
})
export class MyPlansModalComponent implements OnInit, OnDestroy, ModalChildComponent {

    parentComponent: ModalComponent;
    entityId: string;
    fnfBreadcrumb = [];
    modalData: any;
    emptySchedules = false;
    timesList = [];
    selectOptions = [];
    times: string;
    destroy$ = new Subject<void>();
    fnfList = [];
    fnfHash: Map<string, any> = new Map();
    myPlan = new MyPlan();
    selectedDate: any;
    showCheckAll = false;
    selectAllChecked = false;
    someGuestsSelected = false;

    private checkedGuest$ = new Subject<AvatarCheckboxEvent>();

    // @TODO: These needs to become pipes
    get fnfState() {
        return this.fnfList.length ? 'displayed' : '';
    }

    get keyringServiceLoading() {
        return this.keyringService.isLoading;
    }

    get disableButton() {
        return !this.myPlan.startDate || this.scheduleService.isLoading || this.keyringServiceLoading || !this.fnfList.length;
    }

    get saved() {
        return this.myPlansService.saved;
    }
    // @TODO: These needs to become pipes

    constructor(
        public myPlansService: MyPlansService,
        public authenticationService: AuthenticationService,
        public scheduleService: ScheduleService,
        private keyringService: KeyringService,
        private elementRef: ElementRef,
        private debugLog: DebugLogService,
    ) {}

    ngOnInit() {
        this.modalData = this.myPlansService.modalData;
        const swid = this.myPlansService.swid;
        this.entityId = this.getEntityId();
        this.fnfBreadcrumb.push(this.modalData.fnf.breadcrumb);
        this.setupTimes();

        this.scheduleService.scheduleUpdated
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.setupTimes());

        this.keyringService.getFnFList(swid)
            .pipe(takeUntil(this.destroy$))
            .subscribe((list: any) => this.setUpFnf(list));

        this.setupCheckedGuestListener();
    }

    ngOnDestroy() {
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    // @TODO: This needs to become a pipe
    getSelectedGuests() {
        /**
         * This is called on the success screen once the guests are submitted to the plans endpoint.
         */
        return this.myPlan.guests.map((guestId) => this.fnfHash.get(guestId));
    }

    // @TODO: This needs to become a pipe
    guestAge(guest) {
        const fnfLabels = this.modalData.fnf;

        return guest.primaryGuest ? fnfLabels.meLabel : `${fnfLabels.ageLabel} ${guest.age}`;
    }

    getEntityId() {
        const splitValue = this.modalData.entity.id.split(';');

        return splitValue[0];
    }

    setupTimes() {
        let performanceTimesSchedules;
        this.timesList = [];
        if (this.scheduleService.schedule && !isEmpty(this.scheduleService.schedule.schedules)) {
            performanceTimesSchedules = this.scheduleService.schedule.schedules[SCHEDULE_TYPES.performanceTime];
            reduce(performanceTimesSchedules, (result, value, key) => {
                value.forEach(element => {
                    result.push(element);
                });

                return result;
            }, this.timesList);
            this.selectOptions = this.timesList.map(time => time.startTimeOutput);
            this.times = this.selectOptions.length <= SCHEDULE_CONSTANTS.scheduleTimesThreshold ? 'buttons' : 'select';
            this.emptySchedules =  this.timesList.length === 0;
        } else {
            this.emptySchedules = true;
        }
    }

    setLabelsForGuest(guest) {
        const fnfLabels = this.modalData.fnf;
        const labels = {
            pendingMerge: fnfLabels.pendingMerge,
            profileManagedBy: fnfLabels.profileManagedBy,
            profileManagedByYou: fnfLabels.profileManagedByYou
        };
        guest.labels = labels;
    }

    setUpFnf(fnfList) {
        // Find primaryGuest
        const primaryGuest = fnfList.friendsAndFamily.find(fnf => {
            // Mark the primary guest as checked
            fnf.checked = fnf.primaryGuest;

            return fnf.primaryGuest;
        });
        // Add it to the service payload
        this.myPlan.primaryGuest = primaryGuest.xid;
        // Set List
        this.fnfList = Object.values(fnfList.friendsAndFamily);
        // Setup hash
        this.fnfList.forEach(fnf => this.fnfHash.set(fnf.xid, fnf));
        // Add labels if needed
        this.fnfList.forEach(guest => {
            if (guest.pendingMergeRequestIds?.length) {
                this.setLabelsForGuest(guest);
            }
        });
        // Check to see if there is more than 1 person in the list. If so add the select all option
        this.showCheckAll = this.fnfList.length > 0;
    }

    onDateChanged(event) {
        this.scheduleService.date = event;
        this.scheduleService.getSchedules(event);
    }

    onCalendarClose(event) {
        this.elementRef.nativeElement.querySelectorAll('.calendar-button input').forEach(elm => elm.focus());
    }

    onSelectedTime(startTime) {
        const date = this.timesList.find(time => time.startTimeOutput === startTime);
        this.setDate(date);
    }

    setDate(time) {
        this.selectedDate = time;
        this.myPlan.startDate = `${time.date}T${time.startTime}Z`;
        this.myPlan.endDate = `${time.date}T${time.endTime}Z`;
        this.myPlan.timezone = get(this.scheduleService, 'schedule.timeZone');
    }

    isTimeSelected(time) {
        /**
         * @TODO:
         * This actually needs to be refactored to set a property the button can check.
         * Right now it's being called repeatedly.
         */
        return (this.myPlan.startDate && this.myPlan.endDate)
            && (this.myPlan.startDate === `${time.date}T${time.startTime}Z`)
            && (this.myPlan.endDate === `${time.date}T${time.endTime}Z`);
    }

    onCheckedGuest(event: AvatarCheckboxEvent) {
        // Check for the shiftKey event. This means we're selecting a group of guests
        if (event.shiftKey && event.checked) {
            this.groupSelectGuests(event);

            return;
        }

        this.checkedGuest$.next(event);
    }

    groupSelectGuests(event: AvatarCheckboxEvent) {
        // Start at the beginning of the fnf list and keep going until we find the guest in the event
        this.fnfList.find(fnf => {
            // This will enable the checkbox
            fnf.checked = true;
            // This will update the array of guests
            this.checkedGuest$.next({
                checked: true,
                guestId: fnf.xid
            });

            // Stop once we've found the guest where shift was pressed
            if (fnf.xid === event.guestId) {
                return true;
            }
        });
    }

    onSelectAllChange(event) {
        const checked = get(event, 'detail.value');
        this.selectAllChecked = checked;

        if (checked) {
            // Select everything
            this.fnfList.forEach(fnf => {
                if (!fnf.disabled) {
                    fnf.checked = true;
                }
            });

            return;
        }

        // Don't unselect all the guests if some are selected
        if (this.someGuestsSelected) {
            return;
        }

        // Unselect everything
        this.fnfList.forEach(fnf => {
            // Don't uncheck the primary guest
            if (!fnf.disabled) {
                fnf.checked = fnf.primaryGuest || false;
            }
        });

        return;
    }

    updateSelectAllCheckbox() {
        const allChecked = this.fnfList.every(fnf => fnf.disabled || fnf.checked);
        const someChecked = this.fnfList.some(fnf => !fnf.disabled && fnf.checked);

        this.selectAllChecked = allChecked;
        this.someGuestsSelected = someChecked && !allChecked;
    }

    save() {
        this.myPlansService.save(this.myPlan, this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.parentComponent.hideHeader = true);
    }

    dismiss() {
        this.parentComponent.dismiss();
    }

    private setupCheckedGuestListener() {
        this.checkedGuest$.asObservable()
            .pipe(
                map((event: AvatarCheckboxEvent) => {
                    // Get the guest object
                    const guest = this.fnfHash.get(event.guestId);
                    // Mark their status
                    guest.checked = event.checked;

                    return event;
                }),
                takeUntil(this.destroy$),
                /**
                 * When the modal is loaded the checkbox event spams the onCheckedGuest method.
                 * This will wait until all the events have passed and create the list of swids
                 * once.
                 */
                debounceTime(100)
            )
            .subscribe(() => {
                // Reset the list sent to the fnf service
                this.myPlan.guests.length = 0;

                // Create a clean list
                this.fnfHash.forEach(fnf => {
                    if (fnf.checked) {
                        this.myPlan.guests.push(fnf.xid);
                    }
                });

                /**
                 * When all the guests are selected and then one is unchecked the selectAll code
                 * will try to deselect everything. This stops that.
                 */
                this.someGuestsSelected = this.myPlan.guests.length > 0 && this.myPlan.guests.length !== this.fnfList.length;

                // update "select all" checkbox state
                this.updateSelectAllCheckbox();

                // Log the guest list when debug is on
                this.debugLog.log(`guest list`, this.myPlan.guests);
            });
    }
}
